// @flow

import * as t from "./backend/manufacturing2.generated.types";
import { translate } from "../components/IntlProviderWrapper";
import { INSIGHT_TYPES } from "./ManufacturingTags.generated";
import type { IEventDataEx } from "../lib/backend/reports.generated.types";

import {
    ROLE_ADMIN,
    ROLE_MAN_SHOPFLOOR_MANAGER,
    ROLE_POWER_USER,
    ROLE_DEMO_USER,
    ROLE_MAN_PG_VIEWER,
    ROLE_MAN_DT_VIEWER,
    ROLE_MAN_PLANNER,
    isInRole,
} from "./Auth";
import { createHtmlAndDownload } from "./Util";
import {
    ORANGE_COLOR,
    DARK_SALMON_COLOR,
    DARK_BLUE,
    GREEN_COLOR,
    WHITE_COLOR,
    GREY_COLOR,
    LIGHT_BLUE,
    DARKER_SALMON_COLOR,
    DARKER_DARK_SALMON_COLOR,
    DARKER_LIGHT_BLUE,
    DARKER_DARK_BLUE,
    DARKER_WHITE_COLOR,
    DARKER_GREY_COLOR,
} from "./Colors";
import local_settings, {
    FIELD_INSIGHTS_SELECTION_SHOWCHECKBOXES,
    FIELD_INSIGHTS_SELECTION_LINEUUIDS,
    FIELD_INSIGHTS_SELECTION_EVENTTYPES,
    FIELD_INSIGHTS_SELECTION_LINEUUIDS_WIP,
    FIELD_INSIGHTS_SELECTION_EVENTTYPES_WIP,
} from "./LocalSettings";

export const INSIGHT_FILTERS = {
    filter_account_for_deliveries: "filter_account_for_deliveries",
    filter_include_verp: "filter_include_verp",
    filter_use_safety_stock: "filter_use_safety_stock",
};

export const AVAILABLE_TYPES = [
    INSIGHT_TYPES.tool_setup,
    INSIGHT_TYPES.tool_overlap,
    INSIGHT_TYPES.man_downtime,
    INSIGHT_TYPES.operation_no_input_material,
    INSIGHT_FILTERS.filter_use_safety_stock,
    INSIGHT_FILTERS.filter_account_for_deliveries,
    INSIGHT_FILTERS.filter_include_verp,
    INSIGHT_TYPES.operation_delay_downstream,
    INSIGHT_TYPES.operation_constraint_violated,
    INSIGHT_TYPES.operation_exceeds_deadline,
    INSIGHT_TYPES.freeze_order_move,
    INSIGHT_TYPES.conditional_capacity_violated,
    INSIGHT_TYPES.planned_date_diff,
    INSIGHT_TYPES.partially_scheduled,
];

export type AvailableInsightTypes =
    | typeof INSIGHT_TYPES.tool_setup
    | typeof INSIGHT_TYPES.tool_overlap
    | typeof INSIGHT_TYPES.man_downtime
    | typeof INSIGHT_TYPES.operation_no_input_material
    | typeof INSIGHT_FILTERS.filter_use_safety_stock
    | typeof INSIGHT_FILTERS.filter_account_for_deliveries
    | typeof INSIGHT_FILTERS.filter_include_verp
    | typeof INSIGHT_TYPES.operation_delay_downstream
    | typeof INSIGHT_TYPES.operation_constraint_violated
    | typeof INSIGHT_TYPES.operation_exceeds_deadline
    | typeof INSIGHT_TYPES.freeze_order_move
    | typeof INSIGHT_TYPES.conditional_capacity_violated
    | typeof INSIGHT_TYPES.planned_date_diff
    | typeof INSIGHT_TYPES.partially_scheduled;

export const getColorPatternForPlannedOrders = (color: string): string => {
    if (color === ORANGE_COLOR) return "circlePatternSalmon";
    if (color === DARK_SALMON_COLOR) return "circlePatternDarkSalmon";
    if (color === WHITE_COLOR) return "circlePatternWhite";
    if (color === GREY_COLOR) return "circlePatternGrey";
    if (color === LIGHT_BLUE) return "circlePatternLightBlue";
    if (color === DARK_BLUE) return "circlePatternDarkBlue";
    return color;
};

export const getDarkerColor = (color: string): string => {
    if (color === ORANGE_COLOR) return DARKER_SALMON_COLOR;
    if (color === DARK_SALMON_COLOR) return DARKER_DARK_SALMON_COLOR;
    if (color === LIGHT_BLUE) return DARKER_LIGHT_BLUE;
    if (color === DARK_BLUE) return DARKER_DARK_BLUE;
    if (color === WHITE_COLOR) return DARKER_WHITE_COLOR;
    if (color === GREY_COLOR) return DARKER_GREY_COLOR;
    return color;
};

export function storeInsightsShowCheckboxesOption(show: boolean): void {
    local_settings.setItem(FIELD_INSIGHTS_SELECTION_SHOWCHECKBOXES, JSON.stringify(show));
}

export function getInsightsShowCheckboxesOption(): boolean {
    const show = local_settings.getItem(FIELD_INSIGHTS_SELECTION_SHOWCHECKBOXES);
    if (show === undefined || show === null) {
        return false;
    }
    return JSON.parse(show);
}

export function storeInsightsSelectionLineUUIDs(line_uuids: Object): void {
    local_settings.setItem(FIELD_INSIGHTS_SELECTION_LINEUUIDS, JSON.stringify(line_uuids));
}

export function storeInsightsSelectionEventTypes(event_types: Object): void {
    local_settings.setItem(FIELD_INSIGHTS_SELECTION_EVENTTYPES, JSON.stringify(event_types));
}

export function setInsightSelectionLineUUIDsWIP(status: boolean) {
    local_settings.setItem(FIELD_INSIGHTS_SELECTION_LINEUUIDS_WIP, status.toString());
}

export function setInsightsSelectionEventTypesWIP(status: boolean) {
    local_settings.setItem(FIELD_INSIGHTS_SELECTION_EVENTTYPES_WIP, status.toString());
}

export function getInsightsSelectionWIP() {
    const eventtypesWIP = local_settings.getItem(FIELD_INSIGHTS_SELECTION_EVENTTYPES_WIP);
    const lineuuidsWIP = local_settings.getItem(FIELD_INSIGHTS_SELECTION_LINEUUIDS_WIP);
    if (eventtypesWIP && lineuuidsWIP) {
        return JSON.parse(eventtypesWIP) || JSON.parse(lineuuidsWIP);
    }
    return true;
}

function cleanEmptyKeys(ids: any) {
    if (ids) {
        return Object.keys(ids).reduce((obj, key) => {
            if (key !== "") {
                obj[key] = ids[key];
            }
            return obj;
        }, {});
    }
    return {};
}

export function getInsightSelectionLineUUIDs() {
    const selection = local_settings.getItem(FIELD_INSIGHTS_SELECTION_LINEUUIDS);
    if (selection === undefined || selection === null) {
        return {};
    }
    return cleanEmptyKeys(JSON.parse(selection));
}

export function getInsightSelectionEventTypes() {
    const selection = local_settings.getItem(FIELD_INSIGHTS_SELECTION_EVENTTYPES);
    if (selection === undefined || selection === null) {
        return {};
    }
    return cleanEmptyKeys(JSON.parse(selection));
}

export function insightTypeToName(insight_type: string): string {
    const array1 = insight_type.split("_");
    const name1 = array1.join(" ");
    const array2 = name1.split(".");
    const name2 = array2.join(" ").replace(/  +/g, " ");
    return name2.charAt(0).toUpperCase() + name2.slice(1);
}

export function getDefaultInsightTypesForUser(): string[] {
    if (isInRole(ROLE_ADMIN) || isInRole(ROLE_POWER_USER) || isInRole(ROLE_DEMO_USER)) {
        return [
            INSIGHT_TYPES.freeze_order_move,
            INSIGHT_TYPES.no_person,
            INSIGHT_TYPES.no_shift,
            INSIGHT_TYPES.no_work_in_shift,
            INSIGHT_TYPES.out_of_stock_shopfloor,
            INSIGHT_TYPES.out_of_stock_plant,
        ];
    }

    if (isInRole(ROLE_MAN_PLANNER) || isInRole(ROLE_MAN_PG_VIEWER) || isInRole(ROLE_MAN_DT_VIEWER)) {
        return [
            INSIGHT_TYPES.freeze_order_move,
            INSIGHT_TYPES.man_downtime,
            INSIGHT_TYPES.no_person,
            INSIGHT_TYPES.no_shift,
            INSIGHT_TYPES.no_work_in_shift,
            INSIGHT_TYPES.out_of_stock_shopfloor,
            INSIGHT_TYPES.out_of_stock_plant,
        ];
    }

    if (isInRole(ROLE_MAN_SHOPFLOOR_MANAGER)) {
        return [
            INSIGHT_TYPES.man_downtime,
            INSIGHT_TYPES.no_person,
            INSIGHT_TYPES.no_shift,
            INSIGHT_TYPES.no_work_in_shift,
            INSIGHT_TYPES.out_of_stock_shopfloor,
            INSIGHT_TYPES.tool_setup,
        ];
    }

    // default
    return [
        INSIGHT_TYPES.freeze_order_move,
        INSIGHT_TYPES.no_person,
        INSIGHT_TYPES.no_shift,
        INSIGHT_TYPES.no_work_in_shift,
        INSIGHT_TYPES.out_of_stock_shopfloor,
        INSIGHT_TYPES.out_of_stock_plant,
    ];
}

export type InsightDayShiftGrid = {
    "1": t.IEventDataEx[],
    "2": t.IEventDataEx[],
    "3": t.IEventDataEx[],
};

export type InsightWeekShiftGrid = {
    "1": InsightDayShiftGrid,
    "2": InsightDayShiftGrid,
    "3": InsightDayShiftGrid,
    "4": InsightDayShiftGrid,
    "5": InsightDayShiftGrid,
    "6": InsightDayShiftGrid,
    "7": InsightDayShiftGrid,
};

export function getEmptyWeekShiftGrid(): InsightWeekShiftGrid {
    return {
        "1": { "1": [], "2": [], "3": [] },
        "2": { "1": [], "2": [], "3": [] },
        "3": { "1": [], "2": [], "3": [] },
        "4": { "1": [], "2": [], "3": [] },
        "5": { "1": [], "2": [], "3": [] },
        "6": { "1": [], "2": [], "3": [] },
        "7": { "1": [], "2": [], "3": [] },
    };
}

export function exportInsightsHTML(html_string: string): void {
    const html = `
        <html>
            <head>
                <title>${translate("Header.menu.insights")}</title>
                <meta charSet="utf-8" />
                <style>
                    body {
                        font-family: "Arial", "Helvetica", "sans-serif";
                    }

                    body, table, p, div {
                        font-size: 12px;
                    }

                    dl,
                    dd,
                    dt {
                        font-size: 100%;
                        font: inherit;
                        vertical-align: baseline;
                        margin: 0;
                        min-width: 50px;
                    }

                    .insight__table {
                        margin-bottom: 0;
                    }

                    .insight__table .insight__table-cell {
                        display: flex;
                        gap: 8px;
                        padding: 2px 8px;
                        color: #616e7c;
                        border-bottom: 1px solid #e6e6e6;
                        border-left: 1px solid #e6e6e6;

                        &:first-child {
                            padding-top: 8px;
                        }

                        &:last-child {
                            padding-bottom: 8px;
                            border-right: 1px solid #e6e6e6;
                        }
                    }

                    .insight__table .insight__table-cell.child:last-child {
                        text-align: left;
                    }

                    .insight__table dl .insight__table-cell:last-child {
                        border-bottom: 1px solid #e6e6e6;
                    }

                    .insight__table .insight__table-head .insight__table-cell {
                        border-top: 1px solid #e6e6e6;
                    }

                    .insight__table .th-wrapper,
                    .insight__table .td-wrapper {
                        padding: 0;
                    }

                    .insight__table .th-wrapper>div,
                    .insight__table .td-wrapper>div {
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 100%;
                        height: 100%;
                        padding: .65rem;
                    }

                    .insight__table .insight__table-cell strong {
                        font-weight: 600;
                    }

                    .insight__table .toggle-historical {
                        background-color: transparent !important;
                    }

                    .insight__table .toggle-historical .insight__table-cell {
                        padding: 15px 0;
                    }

                    .insight__table .insight__table-body .insight-description {
                        display: flex;
                        gap: 16px;
                        height: 100%;
                    }

                    .insight__calendar-icon {
                        display: none;
                    }

                    .insight__table div.insight__icon-wrapper {
                        display: none;
                    }

                    .insight__description {
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        gap: 8px;
                        margin-top: 8px;
                    }

                    .insight__actions {
                        text-align: right;
                    }

                    .insight__table-cell:last-child {
                        dt {
                            display: none;
                        }
                    }

                    .insight__table .insight__table-body dl.dayTitle .insight__table-cell {
                        color: #fff;
                        background-color: #7391ab;
                    }

                    .insight__table {
                        display: table;
                        width: 100%;
                    }

                    .insight__table-body {
                        display: table-row-group;
                    }

                    .insight__table-head {
                        display: table-header-group;
                    }

                    .insight__table-row {
                        display: table-row;
                        width: 100%;
                    }

                    .insight__table .insight__table-cell {
                        display: table-cell;
                        vertical-align: top;
                    }

                    .insight__table .insight__table-body dl.dayTitle {
                        position: relative;
                        height: 40px;
                    }

                    .insight__table .insight__table-body dl.dayTitle .insight__table-cell:first-child {
                        position: absolute;
                        inset: 0;
                        display: flex;
                        align-items: center;
                    }

                    .insight__table dl .insight__table-cell dt {
                        display: none;
                    }

                    .insight__table dl .insight__table-cell {
                        border-bottom: 1px solid #e6e6e6;
                    }

                    .insight__table .insight__table-head .insight__table-cell {
                        font-size: 16px;
                        color: #3e4c59;
                        font-weight: 400;
                        border: 0;
                        border-bottom: 1px solid #e6e6e6;
                        vertical-align: middle;
                    }

                    .insight__table-head {
                        position: relative;
                        top: auto;
                    }

                    .table .insight__table-cell {
                        padding: 8px 15px;
                    }

                    .insight__table-head .container-fluid {
                        padding-right: 15px;
                        padding-left: 15px;
                    }

                    .insight__description {
                        margin-top: 0;
                    }

                    .insight__description {
                        flex-direction: row;
                        align-items: center;
                        gap: 16px;
                    }

                    .insight__table .insight__table-cell {
                        vertical-align: middle;
                    }

                    .insight__table .insight__table-body .insight-description {
                        align-items: center;
                        gap: 24px;
                    }

                    @media print { body { -webkit-print-color-adjust: exact; } }
                </style>
            </head>
            <body onload="window.print()">
                ${html_string}
            </body>
        </html>
    `;

    createHtmlAndDownload(html, "insights.html");
}

// Type for orderColor function arguments
type OrderColorArgs = {
    order_type: string,
    has_insight: boolean,
    failed_scheduling?: boolean,
    filtered_insight_types?: AvailableInsightTypes[],
    insights?: IEventDataEx[],
    is_unreleased?: boolean,
    is_cleaning_order?: boolean,
};

export const orderColor = (args: OrderColorArgs): string => {
    const {
        order_type,
        has_insight,
        failed_scheduling,
        filtered_insight_types = [],
        insights = [],
        is_unreleased = false,
        is_cleaning_order = false,
    } = args;

    if (failed_scheduling) {
        return "white";
    }
    const is_planned = order_type === "plan";
    const show_pattern = is_planned || is_unreleased;

    if (is_cleaning_order) {
        return GREEN_COLOR;
    }

    // check if we have any worthy insight
    if (has_insight && insights.length > 0) {
        const insight_types = insights
            .map(insight => insight.type)
            .filter(insight_type => filtered_insight_types.includes(insight_type));

        const is_orange = isWarningInsight(insight_types);
        const is_salmon = isErrorInsight(insight_types);

        if (is_orange && is_salmon) {
            return show_pattern ? "url(#patternCombinedPlannedInsights)" : "url(#patternCombinedInsights)";
        } else if (is_orange) {
            return show_pattern ? `url(#${getColorPatternForPlannedOrders(ORANGE_COLOR)})` : ORANGE_COLOR;
        } else if (is_salmon) {
            return show_pattern ? `url(#${getColorPatternForPlannedOrders(DARK_SALMON_COLOR)})` : DARK_SALMON_COLOR;
        }
    }

    // default is blue
    return show_pattern ? `url(#${getColorPatternForPlannedOrders(DARK_BLUE)})` : DARK_BLUE;
};

export const isWarningInsight = (insight_types: AvailableInsightTypes[]): boolean =>
    insight_types.includes(INSIGHT_TYPES.operation_no_input_material) ||
    insight_types.includes(INSIGHT_TYPES.tool_overlap);

export const isErrorInsight = (insight_types: AvailableInsightTypes[]): boolean =>
    insight_types.includes(INSIGHT_TYPES.operation_delay_downstream) ||
    insight_types.includes(INSIGHT_TYPES.operation_constraint_violated) ||
    insight_types.includes(INSIGHT_TYPES.conditional_capacity_violated) ||
    insight_types.includes(INSIGHT_TYPES.planned_date_diff);
